import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MapPin, CheckCircle, XCircle, Loader2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface ProjectMapItPanelProps {
  orgId: string | null;
}

interface PMIIntegration {
  id: string;
  api_key: string | null;
  client_id: string | null;
  is_active: boolean;
}

export function ProjectMapItPanel({ orgId }: ProjectMapItPanelProps) {
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<PMIIntegration | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [accountId, setAccountId] = useState("");
  const [autoSync, setAutoSync] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<"connected" | "disconnected" | "testing">("disconnected");

  useEffect(() => {
    if (orgId) {
      loadSettings();
    }
  }, [orgId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("supplier_integrations")
        .select("*")
        .eq("org_id", orgId)
        .eq("supplier_name", "project_mapit")
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setIntegration({
          id: data.id,
          api_key: data.api_key,
          client_id: data.client_id,
          is_active: data.is_active ?? false,
        });
        setApiKey(data.api_key || "");
        setAccountId(data.client_id || "");
        setAutoSync(data.preferred_branch_code === "auto_sync");
        setConnectionStatus(data.is_active ? "connected" : "disconnected");
      }
    } catch (error) {
      console.error("Error loading PMI settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiKey || !accountId) {
      toast.error("Please enter both API Key and Account ID");
      return;
    }

    setTesting(true);
    setConnectionStatus("testing");

    try {
      const { data, error } = await supabase.functions.invoke("project-mapit-sync", {
        body: {
          action: "test_connection",
          apiKey,
          accountId,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setConnectionStatus("connected");
        toast.success("Successfully connected to Project Map It!");
      } else {
        setConnectionStatus("disconnected");
        toast.error(data?.message || "Failed to connect");
      }
    } catch (error: any) {
      console.error("Connection test failed:", error);
      setConnectionStatus("disconnected");
      toast.error("Connection test failed. Check your credentials.");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!orgId) return;

    setSaving(true);
    try {
      const integrationData = {
        org_id: orgId,
        supplier_name: "project_mapit",
        api_key: apiKey,
        client_id: accountId,
        preferred_branch_code: autoSync ? "auto_sync" : null,
        is_active: connectionStatus === "connected",
      };

      if (integration?.id) {
        const { error } = await supabase
          .from("supplier_integrations")
          .update(integrationData)
          .eq("id", integration.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("supplier_integrations")
          .insert(integrationData);

        if (error) throw error;
      }

      toast.success("Project Map It settings saved!");
      await loadSettings();
    } catch (error: any) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-green-500">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Project Map It</CardTitle>
              <CardDescription>
                Visual portfolio & sales enablement platform
              </CardDescription>
            </div>
          </div>
          <Badge variant={connectionStatus === "connected" ? "default" : "secondary"}>
            {connectionStatus === "connected" ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 mr-1" />
                Not Connected
              </>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Partner info */}
        <div className="p-4 rounded-lg bg-muted/50 border">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Project Map It</span> helps contractors 
            build trust with homeowners by showcasing completed projects on an interactive map. 
            Push completed jobs directly to your PMI portfolio with one click.
          </p>
          <a 
            href="https://projectmapit.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
          >
            Learn more about Project Map It
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Credentials */}
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="pmi-api-key">API Key</Label>
            <div className="relative">
              <Input
                id="pmi-api-key"
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your Project Map It API key"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pmi-account-id">Account ID</Label>
            <Input
              id="pmi-account-id"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="Enter your PMI Account ID"
            />
          </div>
        </div>

        {/* Auto-sync option */}
        <div className="flex items-center justify-between p-4 rounded-lg border">
          <div>
            <p className="font-medium text-sm">Auto-sync completed jobs</p>
            <p className="text-xs text-muted-foreground">
              Automatically push jobs to Project Map It when marked as completed
            </p>
          </div>
          <Switch
            checked={autoSync}
            onCheckedChange={setAutoSync}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={testing || !apiKey || !accountId}
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
